/**
 * FaceIdDialog.tsx - Модальное окно входа по FaceID с многоуровневой защитой от спуфинга (Anti-Spoofing & Active Liveness Detection)
 * Корпоративный портал Zerde Pharmacy
 *
 * Уровни защиты от обхода по фотографии / видеозаписи:
 * 1. Серверное криптографическое рукопожатие: получение одноразового challengeToken с динамической последовательностью.
 * 2. Обязательная детекция моргания (Eye Aspect Ratio - EAR): фото не способно моргнуть.
 * 3. Относительный поворот головы (Relative Delta Yaw) с проверкой непрерывной траектории и времени.
 * 4. Детекция мимики / улыбки (Mouth Aspect Ratio - MAR): тест динамического расширения уголков рта.
 * 5. Anti-Swap фильтр: отслеживание скачков координат и потери лица для предотвращения подмены карточек/фото.
 * 6. Серверный инференс (InsightFace / ArcFace + Anti-Spoofing).
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Slide,
  IconButton,
  CircularProgress,
  LinearProgress,
  Alert,
  Chip,
  Collapse
} from '@mui/material';
import {
  Close,
  Refresh,
  CheckCircle,
  WarningAmber,
  Visibility,
  ArrowBack,
  ArrowForward,
  SentimentSatisfiedAlt,
  ScreenRotation,
  ArrowDownward,
  ZoomIn,
  TimerOutlined,
  QrCode2,
  PhoneIphone,
  TerminalOutlined,
  Memory
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { ScanFace, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { saveUserSession } from '../utils/sessionManager';
import { API_CONFIG } from '../config/authConfig';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: (userData: any) => void;
}

export type LivenessAction = 'turn_left' | 'turn_right' | 'smile' | 'tilt_head' | 'nod' | 'move_closer';
export type AuthStep =
  | 'loading_models'
  | 'starting_camera'
  | 'centering'
  | 'challenge'
  | 'matching'
  | 'qr_code'
  | 'success'
  | 'error';

const FaceIdDialog: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const [step, setStep] = useState<AuthStep>('loading_models');
  const [statusMessage, setStatusMessage] = useState<string>('Инициализация сессии защиты...');
  const [subMessage, setSubMessage] = useState<string>('');
  const [progress, setProgress] = useState<number>(10);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(30);

  // Список динамических действий испытания живости
  const [actionsSequence, setActionsSequence] = useState<LivenessAction[]>([]);
  const [currentActionIndex, setCurrentActionIndex] = useState<number>(0);
  const [completedActions, setCompletedActions] = useState<LivenessAction[]>([]);

  // Телеметрия нейросети и журнал событий в реальном времени
  const [modelInfo, setModelInfo] = useState<{ detector: string; recognizer: string; landmarks: string; runtime: string } | null>({
    detector: 'SCRFD-10G (InsightFace)',
    recognizer: 'ArcFace ResNet-50 (512-dim)',
    landmarks: 'SCRFD 5-KPS + 2D-106',
    runtime: 'ONNX Runtime (Server)'
  });
  const [telemetry, setTelemetry] = useState<{
    model: string;
    detector: string;
    confidence: number;
    yawOffset: number;
    mar: number;
    mouthOpen: number;
    latencyMs: number;
  } | null>(null);
  const [logs, setLogs] = useState<Array<{ id: string; time: string; message: string; type: 'info' | 'success' | 'warning' }>>([]);
  const [showLogs, setShowLogs] = useState<boolean>(true);
  const logsContainerRef = useRef<HTMLDivElement>(null);

  const addLog = (message: string, type: 'info' | 'success' | 'warning' = 'info') => {
    const time = new Date().toLocaleTimeString('ru-RU', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLogs((prev) => {
      if (prev.length > 0 && prev[prev.length - 1].message === message) {
        return prev;
      }
      return [...prev.slice(-25), { id: Math.random().toString(36).substring(2), time, message, type }];
    });
  };

  useEffect(() => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);
  const isLoopActiveRef = useRef<boolean>(false);

  // Серверные сессионные токены
  const livenessSessionIdRef = useRef<string | null>(null);
  const challengeTokenRef = useRef<string | null>(null);
  const challengeStartTimeRef = useRef<number>(0);
  const actionsSequenceRef = useRef<LivenessAction[]>([]);
  const completedActionsListRef = useRef<LivenessAction[]>([]);
  const isProcessingFrameRef = useRef<boolean>(false);
  const hiddenCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Сессии QR-авторизации со смартфона
  const [qrToken, setQrToken] = useState<string>('');
  const [qrExpiresAt, setQrExpiresAt] = useState<number>(0);
  const [qrSecondsLeft, setQrSecondsLeft] = useState<number>(180);
  const [isQrLoading, setIsQrLoading] = useState<boolean>(false);
  const qrPollIntervalRef = useRef<number | null>(null);

  const stopQrPolling = () => {
    if (qrPollIntervalRef.current) {
      window.clearInterval(qrPollIntervalRef.current);
      qrPollIntervalRef.current = null;
    }
  };

  // Остановка таймеров и камеры
  const stopAll = () => {
    isLoopActiveRef.current = false;
    isProcessingFrameRef.current = false;
    stopQrPolling();
    if (animFrameRef.current) {
      window.clearTimeout(animFrameRef.current);
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (countdownIntervalRef.current) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
  };

  // Переключение в режим авторизации по QR-коду со смартфона
  const switchToQrCode = async (customReason?: string) => {
    stopAll();
    stopQrPolling();
    setStep('qr_code');
    setIsQrLoading(true);
    setStatusMessage('Вход через смартфон');
    setSubMessage(customReason || 'Отсканируйте QR-код камерой телефона');
    setProgress(50);

    try {
      const res = await axios.post(`${API_CONFIG.BIOMETRICS_V2_URL}/qr/create`);
      if (res.data?.success && res.data?.qrToken) {
        const tokenVal = res.data.qrToken;
        setQrToken(tokenVal);
        setQrExpiresAt(res.data.expiresAt);
        setQrSecondsLeft(Math.floor((res.data.expiresAt - Date.now()) / 1000));
        setIsQrLoading(false);

        // Polling проверки статуса QR-сессии каждые 1.5 секунды
        qrPollIntervalRef.current = window.setInterval(async () => {
          try {
            const statusRes = await axios.get(`${API_CONFIG.BIOMETRICS_V2_URL}/qr/status/${tokenVal}`);
            if (statusRes.data?.success && statusRes.data?.status === 'completed') {
              stopQrPolling();
              const userData = statusRes.data.userData;

              setProgress(100);
              setStep('success');
              setStatusMessage(`Добро пожаловать, ${userData.fullName || userData.username}!`);
              setSubMessage('Вход через смартфон успешно подтвержден');

              saveUserSession(userData);
              if (userData.avatar) {
                localStorage.setItem('userAvatar', userData.avatar);
              }

              setTimeout(() => {
                if (onSuccess) {
                  onSuccess(userData);
                }
                handleClose();
              }, 1000);
            } else if (statusRes.data?.status === 'expired' || statusRes.data?.status === 'invalid') {
              stopQrPolling();
              setErrorMessage('Срок действия QR-кода истек. Пожалуйста, обновите QR-код.');
              setStep('error');
            }
          } catch (pollErr) {
            console.warn('[FaceIdDialog] Ошибка опроса QR-сессии:', pollErr);
          }
        }, 1500);
      } else {
        throw new Error('Сервер не вернул токен QR-сессии');
      }
    } catch (qrErr) {
      console.error('[FaceIdDialog] Ошибка создания QR-сессии:', qrErr);
      setErrorMessage('Не удалось создать сессию QR-кода. Попробуйте еще раз.');
      setStep('error');
      setIsQrLoading(false);
    }
  };

  // Таймер обратного отсчета действия QR-кода
  useEffect(() => {
    if (step === 'qr_code' && qrExpiresAt > 0) {
      const interval = window.setInterval(() => {
        const left = Math.max(0, Math.floor((qrExpiresAt - Date.now()) / 1000));
        setQrSecondsLeft(left);
        if (left <= 0) {
          stopQrPolling();
          setErrorMessage('Время действия QR-кода истекло.');
          setStep('error');
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step, qrExpiresAt]);

  const handleClose = () => {
    stopAll();
    onClose();
  };

  // Подключение к камере
  const requestCameraStream = async (): Promise<MediaStream> => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      throw new Error(
        'Доступ к веб-камере не поддерживается браузером или страница открыта не через HTTPS. Убедитесь, что в адресной строке используется https:// или localhost.'
      );
    }

    if (mediaStreamRef.current) {
      try {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      } catch (e) {
        console.warn('Ошибка остановки старого потока:', e);
      }
      mediaStreamRef.current = null;
    }

    let videoDevices: MediaDeviceInfo[] = [];
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      videoDevices = devices.filter((d) => d.kind === 'videoinput');
    } catch (e) {
      console.warn('[FaceIdDialog] enumerateDevices error:', e);
    }

    const isExcluded = (label: string) => /laserjet|scanner|printer|obs virtual|m426|fax/i.test(label);
    const isPreferred = (label: string) => /integrated|webcam|camera|камера|front|usb/i.test(label);

    const filtered = videoDevices.filter((d) => !isExcluded(d.label));
    const candidates = (filtered.length > 0 ? filtered : videoDevices).sort((a, b) => {
      const aPref = isPreferred(a.label) ? 1 : 0;
      const bPref = isPreferred(b.label) ? 1 : 0;
      return bPref - aPref;
    });

    let lastError: any = null;
    for (const dev of candidates) {
      try {
        const constraints: MediaStreamConstraints = {
          video: dev.deviceId
            ? {
              deviceId: { ideal: dev.deviceId },
              width: { ideal: 640 },
              height: { ideal: 480 }
            }
            : { width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        return stream;
      } catch (err: any) {
        lastError = err;
      }
    }

    try {
      return await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    } catch (directErr: any) {
      lastError = directErr;
    }

    const msg = String(lastError?.message || lastError?.name || '');
    if (msg.includes('Timeout') || lastError?.name === 'AbortError' || lastError?.name === 'NotReadableError' || lastError?.name === 'TrackStartError') {
      throw new Error('Камера занята другим приложением или вкладкой браузера. Закройте их и повторите попытку.');
    }
    if (lastError?.name === 'NotAllowedError' || lastError?.name === 'PermissionDeniedError') {
      throw new Error('Доступ к веб-камере заблокирован. Разрешите использование камеры в настройках сайта.');
    }
    throw new Error(`Ошибка камеры: ${lastError?.message || lastError?.name || 'Не удалось запустить видео'}`);
  };

  // Захват текущего видеокадра в base64 JPEG
  const captureFrameBase64 = (): string | null => {
    if (!videoRef.current || videoRef.current.paused || videoRef.current.ended) return null;
    const video = videoRef.current;
    if (video.videoWidth === 0 || video.videoHeight === 0) return null;

    if (!hiddenCanvasRef.current) {
      hiddenCanvasRef.current = document.createElement('canvas');
    }
    const canvas = hiddenCanvasRef.current;
    canvas.width = 480;
    canvas.height = 360;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, 480, 360);
    return canvas.toDataURL('image/jpeg', 0.85);
  };

  // Обновление понятных текстовых подсказок в UI
  const updateActionPrompt = (action: LivenessAction) => {
    if (action === 'turn_left') {
      setStatusMessage('Поверните голову слегка влево');
      setSubMessage('Плавный поворот в сторону левого плеча');
    } else if (action === 'turn_right') {
      setStatusMessage('Поверните голову слегка вправо');
      setSubMessage('Плавный поворот в сторону правого плеча');
    } else if (action === 'smile') {
      setStatusMessage('Улыбнитесь');
      setSubMessage('Слегка приподнимите уголки губ');
    } else if (action === 'tilt_head') {
      setStatusMessage('Наклоните голову к плечу');
      setSubMessage('Слегка наклоните голову влево или вправо');
    } else if (action === 'nod') {
      setStatusMessage('Кивните головой вниз');
      setSubMessage('Слегка опустите подбородок');
    } else if (action === 'move_closer') {
      setStatusMessage('Приблизьтесь чуть ближе к камере');
      setSubMessage('Подайтесь немного вперед');
    }
  };

  // Сброс прогресса при подмене или потере лица
  const resetChallengeProgress = (reason: string) => {
    completedActionsListRef.current = [];
    setCompletedActions([]);
    setCurrentActionIndex(0);
    setStep('centering');
    setStatusMessage('Поместите лицо в центр визира');
    setSubMessage(reason);
    setProgress(35);
  };

  // Главный цикл отправки кадров на сервер для инференса
  const runServerLivenessLoop = async () => {
    if (!isLoopActiveRef.current || !videoRef.current) {
      return;
    }

    if (!isProcessingFrameRef.current && livenessSessionIdRef.current) {
      const frame = captureFrameBase64();
      if (frame) {
        isProcessingFrameRef.current = true;
        const tStart = performance.now();
        try {
          const res = await axios.post(`${API_CONFIG.BIOMETRICS_V2_URL}/server/liveness/process-frame`, {
            livenessSessionId: livenessSessionIdRef.current,
            frame
          });
          const latencyMs = Math.round(performance.now() - tStart);

          if (res.data?.success) {
            const data = res.data;
            if (data.telemetry) {
              setTelemetry({
                ...data.telemetry,
                latencyMs
              });
            }

            // ФАЗА: Завершено (дескриптор извлечен на сервере)
            if (data.phase === 'completed') {
              isLoopActiveRef.current = false;
              setStep('matching');
              setStatusMessage('Биометрическая проверка пройдена!');
              setProgress(92);
              addLog('Биометрическая проверка пройдена!', 'success');

              await handleServerFinalVerification();
              return;
            }

            // ФАЗА: Калибровка
            if (data.phase === 'calibrating') {
              setStep('centering');
              if (data.faceDetected) {
                setStatusMessage('Поместите лицо в центр визира');
                const samples = data.calibrationProgress || 0;
                setSubMessage(data.message || `Калибровка черт лица (${samples}/5)...`);
                setProgress(20 + samples * 4);
                if (samples > 0) {
                  addLog(`Калибровка черт лица: ${samples}/5 (ракурс: ${data.telemetry?.yawOffset ?? '0.00'})`, 'info');
                }
              } else {
                setStatusMessage('Лицо не обнаружено');
                setSubMessage('Посмотрите прямо в объектив камеры');
              }
            }

            // ФАЗА: Испытание (Challenge)
            if (data.phase === 'challenge') {
              setStep('challenge');
              if (data.currentAction) {
                updateActionPrompt(data.currentAction);
                const currentIdx = actionsSequenceRef.current.indexOf(data.currentAction);
                if (currentIdx >= 0) {
                  setCurrentActionIndex(currentIdx);
                }
              }

              if (Array.isArray(data.completedActions)) {
                completedActionsListRef.current = data.completedActions;
                setCompletedActions(data.completedActions);
                const totalActions = Math.max(actionsSequenceRef.current.length, 1);
                const prog = 45 + (data.completedActions.length / totalActions) * 45;
                setProgress(prog);
              }

              if (data.actionCompleted) {
                setSubMessage(`Действие выполнено!`);
                addLog(`Действие "${getActionLabel(data.actionCompleted)}" подтверждено сервером`, 'success');
              }
            }
          } else if (res.data?.code === 'ANTI_SWAP_TRIGGERED') {
            resetChallengeProgress('Обнаружено резкое смещение или подмена лица. Держите лицо ровно.');
            addLog('Anti-Swap: зафиксирован скачок координат лица', 'warning');
          }
        } catch (err: any) {
          if (err.response?.data?.code === 'SESSION_EXPIRED') {
            stopAll();
            setErrorMessage('Время сессии проверки истекло. Пожалуйста, повторите попытку.');
            setStep('error');
            addLog('Время сессии проверки истекло', 'warning');
            return;
          }
          console.warn('[FaceIdDialog] Ошибка обработки кадра сервером:', err.message);
        } finally {
          isProcessingFrameRef.current = false;
        }
      }
    }

    if (isLoopActiveRef.current) {
      animFrameRef.current = window.setTimeout(runServerLivenessLoop, 120) as any;
    }
  };

  // Финальная сверка дескриптора на сервере и вход
  const handleServerFinalVerification = async () => {
    stopAll();
    addLog('Сверка ArcFace 512-dim дескриптора с базой сотрудников...', 'info');

    try {
      const res = await axios.post(`${API_CONFIG.BIOMETRICS_V2_URL}/server/verify-and-login`, {
        livenessSessionId: livenessSessionIdRef.current,
        challengeToken: challengeTokenRef.current
      });

      if (res.data?.success && res.data?.sessionToken) {
        setProgress(100);
        setStep('success');
        setStatusMessage(`Добро пожаловать, ${res.data.fullName || res.data.username}!`);
        setSubMessage('Вход успешно выполнен');
        const conf = res.data.confidence ? ` (совпадение ${res.data.confidence}%)` : '';
        addLog(`Личность подтверждена: ${res.data.fullName || res.data.username}${conf}`, 'success');

        saveUserSession(res.data);
        if (res.data.avatar) {
          localStorage.setItem('userAvatar', res.data.avatar);
        }

        setTimeout(() => {
          if (onSuccess) {
            onSuccess(res.data);
          }
          handleClose();
        }, 900);
      } else {
        const errMsg = res.data?.message || 'Лицо не найдено среди зарегистрированных сотрудников.';
        setErrorMessage(errMsg);
        setStep('error');
        addLog(`Не совпало: ${errMsg}`, 'warning');
      }
    } catch (authErr: any) {
      console.error('[FaceIdDialog] Ошибка авторизации:', authErr);
      const msg =
        authErr.response?.data?.message ||
        'Лицо не распознано в системе. Привяжите биометрию в профиле или войдите по паролю.';
      setErrorMessage(msg);
      setStep('error');
      const distInfo = authErr.response?.data?.distance !== undefined ? ` (дистанция ${authErr.response.data.distance}, порог 0.45)` : '';
      addLog(`Ошибка распознавания${distInfo}: ${msg}`, 'warning');
    }
  };

  // Запуск сессии Liveness Challenge
  const startAuthFlow = async () => {
    stopAll();
    setErrorMessage(null);
    setSecondsLeft(30);
    setProgress(10);
    setStep('loading_models');
    setStatusMessage('Инициализация сессии FaceID...');
    setSubMessage('Подключение к серверной нейросети InsightFace');
    setLogs([]);
    addLog('Запуск сессии аутентификации FaceID...', 'info');

    // 1. Запрос серверной сессии Liveness Challenge
    try {
      const chalRes = await axios.post(`${API_CONFIG.BIOMETRICS_V2_URL}/server/liveness/start`);
      if (chalRes.data?.success && chalRes.data?.livenessSessionId) {
        livenessSessionIdRef.current = chalRes.data.livenessSessionId;
        challengeTokenRef.current = chalRes.data.challengeToken;
        actionsSequenceRef.current = chalRes.data.sequence || [];
        completedActionsListRef.current = [];
        challengeStartTimeRef.current = Date.now();

        if (chalRes.data.modelInfo) {
          setModelInfo(chalRes.data.modelInfo);
          addLog(`Модели активированы: ${chalRes.data.modelInfo.recognizer}`, 'success');
        } else {
          addLog('Сессия liveness создана, модели готовы', 'success');
        }

        setActionsSequence(chalRes.data.sequence || []);
        setCurrentActionIndex(0);
        setCompletedActions([]);
      } else {
        throw new Error('Сервер не вернул сессию проверки');
      }
    } catch (chalErr) {
      console.error('[FaceIdDialog] Ошибка старта испытания:', chalErr);
      setErrorMessage('Не удалось получить сессию проверки у сервера. Попробуйте еще раз.');
      setStep('error');
      addLog('Не удалось получить сессию проверки у сервера', 'warning');
      return;
    }

    // 2. Подключение камеры
    setStep('starting_camera');
    setStatusMessage('Подключение к веб-камере...');
    setSubMessage('Пожалуйста, подождите');
    setProgress(25);
    addLog('Поиск веб-камеры и запуск видеопотока...', 'info');

    try {
      const stream = await requestCameraStream();
      mediaStreamRef.current = stream;
      addLog(`Камера запущена: ${stream.getVideoTracks()[0]?.label || 'Веб-камера'}`, 'success');

      if (videoRef.current) {
        const video = videoRef.current;
        video.srcObject = stream;

        let loopStarted = false;
        const onStreamReady = () => {
          if (loopStarted) return;
          loopStarted = true;

          // Таймер обратного отсчета (30 секунд)
          countdownIntervalRef.current = window.setInterval(() => {
            setSecondsLeft((prev) => {
              if (prev <= 1) {
                stopAll();
                setErrorMessage('Время проверки истекло. Пожалуйста, повторите попытку.');
                setStep('error');
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          setStep('centering');
          setStatusMessage('Поместите лицо в центр визира');
          setSubMessage('Смотрите прямо в камеру для калибровки');
          setProgress(35);

          isLoopActiveRef.current = true;
          runServerLivenessLoop();
        };

        video.onloadedmetadata = () => {
          video.play().then(onStreamReady).catch((pErr) => {
            console.warn('[FaceIdDialog] video.play() err:', pErr);
            onStreamReady();
          });
        };
        video.play().then(onStreamReady).catch(() => { });
      }
    } catch (camErr: any) {
      console.warn('[FaceIdDialog] Ошибка доступа к камере, переключение на QR:', camErr);
      switchToQrCode('На компьютере не обнаружена веб-камера. Отсканируйте QR-код смартфоном.');
    }
  };

  useEffect(() => {
    if (open) {
      startAuthFlow();
    } else {
      stopAll();
    }
    return () => {
      stopAll();
    };
  }, [open]);

  // Вспомогательная иконка текущего действия
  const renderActionIcon = (action: LivenessAction) => {
    switch (action) {
      case 'turn_left':
        return <ArrowBack sx={{ fontSize: 18 }} />;
      case 'turn_right':
        return <ArrowForward sx={{ fontSize: 18 }} />;
      case 'smile':
        return <SentimentSatisfiedAlt sx={{ fontSize: 18 }} />;
      case 'tilt_head':
        return <ScreenRotation sx={{ fontSize: 18 }} />;
      case 'nod':
        return <ArrowDownward sx={{ fontSize: 18 }} />;
      case 'move_closer':
        return <ZoomIn sx={{ fontSize: 18 }} />;
    }
  };

  const getActionLabel = (action: LivenessAction) => {
    switch (action) {
      case 'turn_left':
        return 'Поворот влево';
      case 'turn_right':
        return 'Поворот вправо';
      case 'smile':
        return 'Улыбка';
      case 'tilt_head':
        return 'Наклон головы';
      case 'nod':
        return 'Кивок головы';
      case 'move_closer':
        return 'Приближение к камере';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          bgcolor: '#09090b',
          backgroundImage: 'linear-gradient(180deg, #18181b 0%, #09090b 100%)',
          color: '#ffffff',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.08)',
          overflow: 'hidden'
        }
      }}
    >
      {/* Верхний бар с таймером и кнопкой закрытия */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 2.5,
          pb: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Box
            sx={{
              p: 0.8,
              borderRadius: '10px',
              bgcolor: 'rgba(16, 185, 129, 0.12)',
              color: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ShieldCheck size={20} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>
              FaceID
            </Typography>

          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {step === 'centering' || step === 'challenge' ? (
            <Chip
              icon={<TimerOutlined sx={{ fontSize: '14px !important', color: secondsLeft <= 10 ? '#ef4444' : '#a1a1aa' }} />}
              label={`${secondsLeft}с`}
              size="small"
              sx={{
                height: 24,
                bgcolor: secondsLeft <= 10 ? 'rgba(239, 68, 68, 0.15)' : 'rgba(255, 255, 255, 0.06)',
                color: secondsLeft <= 10 ? '#f87171' : '#a1a1aa',
                fontWeight: 700,
                fontSize: '0.75rem',
                border: '1px solid rgba(255, 255, 255, 0.08)'
              }}
            />
          ) : null}
          <IconButton onClick={handleClose} size="small" sx={{ color: '#71717a', '&:hover': { color: '#ffffff' } }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Плашка активной AI-модели нейросети */}
      <Box
        sx={{
          mx: 3,
          mb: 1.2,
          p: 0.8,
          px: 1.4,
          borderRadius: '12px',
          bgcolor: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Memory sx={{ fontSize: 16, color: '#10b981' }} />
          <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 700, fontSize: '0.73rem' }}>
            {modelInfo?.recognizer || 'ArcFace ResNet-50 (512-dim)'}
          </Typography>
        </Box>
        <Chip
          label={modelInfo?.detector || 'SCRFD-10G'}
          size="small"
          sx={{
            height: 19,
            fontSize: '0.65rem',
            fontWeight: 700,
            bgcolor: 'rgba(59, 130, 246, 0.15)',
            color: '#60a5fa',
            border: '1px solid rgba(59, 130, 246, 0.3)'
          }}
        />
      </Box>

      <DialogContent sx={{ px: 3, pb: 3, pt: 1, textAlign: 'center' }}>
        {/* Индикаторы шагов испытания */}
        {actionsSequence.length > 0 && (step === 'centering' || step === 'challenge') && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
            <Chip
              size="small"
              label="Центровка"
              color={step === 'challenge' ? 'success' : 'default'}
              variant={step === 'centering' ? 'filled' : 'outlined'}
              sx={{
                height: 26,
                fontSize: '0.75rem',
                fontWeight: 700,
                bgcolor: step === 'centering' ? '#10b981' : undefined
              }}
            />
            {actionsSequence.map((act, i) => {
              const isDone = completedActions.includes(act);
              const isCurrent = step === 'challenge' && currentActionIndex === i;
              return (
                <Chip
                  key={act + i}
                  size="small"
                  icon={isDone ? <CheckCircle sx={{ fontSize: '14px !important', color: '#10b981' }} /> : renderActionIcon(act)}
                  label={`${i + 1}. ${getActionLabel(act)}`}
                  sx={{
                    height: 26,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    bgcolor: isCurrent
                      ? 'rgba(59, 130, 246, 0.2)'
                      : isDone
                        ? 'rgba(16, 185, 129, 0.15)'
                        : 'rgba(255, 255, 255, 0.05)',
                    color: isCurrent ? '#60a5fa' : isDone ? '#34d399' : '#71717a',
                    border: isCurrent
                      ? '1px solid #3b82f6'
                      : isDone
                        ? '1px solid #10b981'
                        : '1px solid rgba(255, 255, 255, 0.08)'
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* Окно видеопотока с лазерным визиром (для ПК камеры) */}
        {step !== 'qr_code' && (
          <Box
            sx={{
              position: 'relative',
              width: 250,
              height: 250,
              mx: 'auto',
              my: 1,
              borderRadius: '50%',
              overflow: 'hidden',
              border:
                step === 'success'
                  ? '3px solid #10b981'
                  : step === 'error'
                    ? '3px solid #ef4444'
                    : step === 'challenge'
                      ? '3px solid #3b82f6'
                      : '3px solid #10b981',
              boxShadow:
                step === 'success'
                  ? '0 0 30px rgba(16, 185, 129, 0.4)'
                  : step === 'error'
                    ? '0 0 30px rgba(239, 68, 68, 0.4)'
                    : '0 0 25px rgba(16, 185, 129, 0.25)',
              bgcolor: '#000000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease'
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scaleX(-1)', // Зеркальное отображение
                display: step === 'success' || step === 'error' ? 'none' : 'block'
              }}
            />

            {/* Успех */}
            {step === 'success' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <CheckCircle sx={{ fontSize: 76, color: '#10b981' }} />
                <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 800 }}>
                  Личность подтверждена!
                </Typography>
              </Box>
            )}

            {/* Ошибка */}
            {step === 'error' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <WarningAmber sx={{ fontSize: 68, color: '#ef4444' }} />
                <Typography variant="body2" sx={{ color: '#ef4444', fontWeight: 700 }}>
                  Доступ отклонен
                </Typography>
              </Box>
            )}

            {/* Анимированный лазерный луч */}
            {step !== 'success' && step !== 'error' && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  height: '2px',
                  background:
                    step === 'challenge'
                      ? 'linear-gradient(90deg, transparent, #60a5fa, transparent)'
                      : 'linear-gradient(90deg, transparent, #34d399, transparent)',
                  boxShadow: step === 'challenge' ? '0 0 12px #3b82f6' : '0 0 12px #34d399',
                  animation: 'scanAnimation 2s ease-in-out infinite',
                  '@keyframes scanAnimation': {
                    '0%': { top: '10%' },
                    '50%': { top: '86%' },
                    '100%': { top: '10%' }
                  }
                }}
              />
            )}
          </Box>
        )}

        {/* БЛОК QR-КОДА ДЛЯ СМАРТФОНА */}
        {step === 'qr_code' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1 }}>
            {isQrLoading ? (
              <Box sx={{ height: 230, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <CircularProgress size={48} sx={{ color: '#10b981' }} />
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  Создание одноразового QR-кода...
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: '#ffffff',
                    borderRadius: '20px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    border: '2px solid rgba(16, 185, 129, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2
                  }}
                >
                  <QRCodeSVG
                    value={`${window.location.origin}/mobile-faceid?token=${qrToken}`}
                    size={200}
                    level="M"
                    includeMargin={false}
                  />
                </Box>

                {/* Таймер действия */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip
                    icon={<TimerOutlined sx={{ fontSize: '15px !important', color: qrSecondsLeft <= 30 ? '#ef4444' : '#10b981' }} />}
                    label={`Код действителен: ${Math.floor(qrSecondsLeft / 60)}:${String(qrSecondsLeft % 60).padStart(2, '0')}`}
                    size="small"
                    sx={{
                      bgcolor: qrSecondsLeft <= 30 ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.12)',
                      color: qrSecondsLeft <= 30 ? '#f87171' : '#34d399',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      border: qrSecondsLeft <= 30 ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid rgba(16, 185, 129, 0.3)'
                    }}
                  />
                </Box>

                <Button
                  variant="text"
                  size="small"
                  startIcon={<Refresh />}
                  onClick={startAuthFlow}
                  sx={{
                    color: '#60a5fa',
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    mt: 0.5,
                    '&:hover': { color: '#93c5fd', bgcolor: 'rgba(59, 130, 246, 0.08)' }
                  }}
                >
                  Попробовать включить камеру на ПК
                </Button>
              </>
            )}
          </Box>
        )}

        {/* Кнопка ручного перехода в режим QR со смартфона */}
        {step !== 'qr_code' && step !== 'success' && step !== 'error' && (
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="text"
              size="small"
              startIcon={<QrCode2 sx={{ fontSize: 18, color: '#34d399' }} />}
              onClick={() => switchToQrCode()}
              sx={{
                color: '#94a3b8',
                textTransform: 'none',
                fontSize: '0.78rem',
                fontWeight: 600,
                '&:hover': { color: '#ffffff', bgcolor: 'rgba(255, 255, 255, 0.05)' }
              }}
            >
              Нет камеры? Войти через смартфон по QR
            </Button>
          </Box>
        )}

        {/* Информационные подсказки */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{
              color: step === 'error' ? '#ef4444' : step === 'success' ? '#34d399' : '#ffffff',
              fontWeight: 800,
              fontSize: '1.05rem',
              lineHeight: 1.25
            }}
          >
            {statusMessage}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#a1a1aa',
              fontSize: '0.82rem',
              mt: 0.5,
              minHeight: 20
            }}
          >
            {subMessage}
          </Typography>

          {step !== 'error' && step !== 'success' && (
            <Box sx={{ width: '85%', mx: 'auto', mt: 1.8 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: step === 'challenge' ? '#3b82f6' : '#10b981',
                    borderRadius: 3
                  }
                }}
              />
            </Box>
          )}
        </Box>

        {/* Панель телеметрии и окно отладочных логов */}
        <Box sx={{ mt: 1.5, mb: 1.5, textAlign: 'left' }}>
          <Box
            onClick={() => setShowLogs((prev) => !prev)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              py: 0.7,
              px: 1.4,
              borderRadius: '10px',
              bgcolor: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              transition: 'all 0.2s ease',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <TerminalOutlined sx={{ fontSize: 16, color: '#38bdf8' }} />
              <Typography variant="caption" sx={{ color: '#cbd5e1', fontWeight: 700, fontSize: '0.74rem' }}>
                Телеметрия нейросети и логи
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {telemetry && (
                <Typography
                  variant="caption"
                  sx={{
                    color: telemetry.confidence > 70 ? '#34d399' : '#fbbf24',
                    fontFamily: 'monospace',
                    fontSize: '0.72rem',
                    fontWeight: 600
                  }}
                >
                  {telemetry.confidence > 0 ? `SCRFD: ${telemetry.confidence}%` : 'Поиск...'}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', userSelect: 'none' }}>
                {showLogs ? 'Скрыть ▲' : 'Развернуть ▼'}
              </Typography>
            </Box>
          </Box>

          <Collapse in={showLogs}>
            <Box
              sx={{
                mt: 1,
                p: 1.2,
                borderRadius: '12px',
                bgcolor: '#05070a',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.5)'
              }}
            >
              {/* Сетка живых метрик телеметрии */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.8, mb: 1 }}>
                <Box sx={{ p: 0.6, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.03)', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.62rem', display: 'block' }}>
                    SCRFD
                  </Typography>
                  <Typography variant="caption" sx={{ color: telemetry && telemetry.confidence > 60 ? '#10b981' : '#94a3b8', fontWeight: 800, fontFamily: 'monospace', fontSize: '0.74rem' }}>
                    {telemetry && telemetry.confidence > 0 ? `${telemetry.confidence}%` : '-'}
                  </Typography>
                </Box>
                <Box sx={{ p: 0.6, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.03)', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.62rem', display: 'block' }}>
                    Ракурс (Yaw)
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#38bdf8', fontWeight: 800, fontFamily: 'monospace', fontSize: '0.74rem' }}>
                    {telemetry ? (telemetry.yawOffset > 0 ? `+${telemetry.yawOffset}` : telemetry.yawOffset) : '0.00'}
                  </Typography>
                </Box>
                <Box sx={{ p: 0.6, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.03)', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.62rem', display: 'block' }}>
                    Мимика (MAR)
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#a78bfa', fontWeight: 800, fontFamily: 'monospace', fontSize: '0.74rem' }}>
                    {telemetry ? telemetry.mar : '0.00'}
                  </Typography>
                </Box>
                <Box sx={{ p: 0.6, borderRadius: '8px', bgcolor: 'rgba(255, 255, 255, 0.03)', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.62rem', display: 'block' }}>
                    Отклик (RTT)
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#fbbf24', fontWeight: 800, fontFamily: 'monospace', fontSize: '0.74rem' }}>
                    {telemetry?.latencyMs ? `${telemetry.latencyMs} мс` : '-'}
                  </Typography>
                </Box>
              </Box>

              {/* Мини-терминал логов */}
              <Box
                ref={logsContainerRef}
                sx={{
                  maxHeight: 90,
                  overflowY: 'auto',
                  pr: 0.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.3,
                  fontSize: '0.7rem',
                  fontFamily: 'Consolas, "Roboto Mono", Menlo, monospace',
                  '&::-webkit-scrollbar': { width: 4 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255, 255, 255, 0.15)', borderRadius: 2 }
                }}
              >
                {logs.length === 0 ? (
                  <Typography variant="caption" sx={{ color: '#475569', fontStyle: 'italic', fontSize: '0.68rem' }}>
                    Ожидание первого кадра...
                  </Typography>
                ) : (
                  logs.map((item) => (
                    <Box key={item.id} sx={{ display: 'flex', gap: 0.8, lineHeight: 1.3 }}>
                      <Typography component="span" sx={{ color: '#475569', fontSize: '0.65rem', flexShrink: 0 }}>
                        {item.time}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          color: item.type === 'success' ? '#34d399' : item.type === 'warning' ? '#f87171' : '#cbd5e1',
                          fontSize: '0.68rem',
                          wordBreak: 'break-word'
                        }}
                      >
                        {item.message}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Collapse>
        </Box>

        {/* Сообщение об ошибке */}
        {errorMessage && (
          <Alert
            severity="error"
            sx={{
              borderRadius: '12px',
              bgcolor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#fca5a5',
              mb: 2.5,
              textAlign: 'left',
              fontSize: '0.85rem'
            }}
          >
            {errorMessage}
          </Alert>
        )}

        {/* Кнопки действий */}
        {step === 'error' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleClose}
                sx={{
                  py: 1.2,
                  borderRadius: '12px',
                  borderColor: '#374151',
                  color: '#9ca3af',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { borderColor: '#4b5563', color: '#fff' }
                }}
              >
                Войти по паролю
              </Button>
              <Button
                variant="contained"
                fullWidth
                startIcon={<Refresh />}
                onClick={startAuthFlow}
                sx={{
                  py: 1.2,
                  borderRadius: '12px',
                  bgcolor: '#10b981',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#059669' }
                }}
              >
                Повторить
              </Button>
            </Box>

            <Button
              variant="contained"
              fullWidth
              startIcon={<PhoneIphone />}
              onClick={() => switchToQrCode()}
              sx={{
                py: 1.1,
                borderRadius: '12px',
                bgcolor: 'rgba(59, 130, 246, 0.2)',
                color: '#60a5fa',
                border: '1px solid #3b82f6',
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.3)' }
              }}
            >
              Войти со смартфона по QR-коду
            </Button>
          </Box>
        ) : step === 'success' ? (
          <Button
            variant="contained"
            fullWidth
            disabled
            sx={{
              py: 1.2,
              borderRadius: '12px',
              bgcolor: '#10b981 !important',
              color: '#fff !important',
              fontWeight: 800
            }}
          >
            Вход в систему...
          </Button>
        ) : (
          <Button
            variant="outlined"
            fullWidth
            onClick={handleClose}
            sx={{
              py: 1.2,
              borderRadius: '12px',
              borderColor: '#27272a',
              color: '#71717a',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { borderColor: '#3f3f46', color: '#a1a1aa' }
            }}
          >
            Отмена
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FaceIdDialog;
